// src/pages/ValesAdmin.js
"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaMoneyBillWave,
  FaFilter,
  FaTicketAlt,
  FaFileDownload,
  FaUndoAlt,
  FaPlus,
  FaPowerOff,
  FaKey,
} from "react-icons/fa";
import "../styles/FinanceiroAdmin.css";

const API = "https://api.ticketdovaqueiro.com/api";

function normalizarForma(forma) {
  const texto = String(forma ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const aliases = {
    voucher: "vale",
    cash: "dinheiro",
    money: "dinheiro",
    card: "cartao",
    credito: "cartao",
    debito: "cartao",
    credit_card: "cartao",
    creditcard: "cartao",
    bank_slip: "boleto",
    check: "cheque",
    ted: "transferencia",
    doc: "transferencia",
  };

  return aliases[texto] || texto;
}

function numero(valor) {
  const resultado = Number(valor);
  return Number.isFinite(resultado) ? resultado : 0;
}

function formasPagamento(pedido = {}) {
  const pagamento = pedido?.pagamento || {};
  const totalPedido = numero(pedido?.total);

  if (Array.isArray(pagamento?.formas) && pagamento.formas.length) {
    const formas = pagamento.formas
      .map((item) => ({
        forma: normalizarForma(item?.forma ?? item?.metodo ?? item?.tipo),
        valor: numero(item?.valor ?? item?.quantia),
        descricaoVale: String(item?.descricaoVale ?? item?.descricao ?? "").trim(),
      }))
      .filter((item) => item.forma && item.valor > 0);

    // Compatibilidade para registros antigos marcados por isVale, mas que ainda
    // não haviam sido convertidos no rateio.
    if (pedido?.isVale === true && !formas.some((item) => item.forma === "vale") && totalPedido > 0) {
      return [{ forma: "vale", valor: totalPedido, descricaoVale: String(pedido?.valeDescricao || "").trim() }];
    }

    if (formas.length) return formas;
  }

  const legadas = [
    { forma: pagamento?.forma1, valor: pagamento?.valor1, descricaoVale: pagamento?.descricaoVale1 },
    { forma: pagamento?.forma2, valor: pagamento?.valor2, descricaoVale: pagamento?.descricaoVale2 },
    { forma: pagamento?.forma3, valor: pagamento?.valor3, descricaoVale: pagamento?.descricaoVale3 },
  ]
    .map((item) => ({
      forma: normalizarForma(item.forma),
      valor: numero(item.valor),
      descricaoVale: String(item.descricaoVale || "").trim(),
    }))
    .filter((item) => item.forma && item.valor > 0);

  if (pedido?.isVale === true && !legadas.some((item) => item.forma === "vale") && totalPedido > 0) {
    return [{ forma: "vale", valor: totalPedido, descricaoVale: String(pedido?.valeDescricao || "").trim() }];
  }

  if (legadas.length) return legadas;

  const forma = normalizarForma(pedido?.metodoPagamento);
  return forma && totalPedido > 0 ? [{ forma, valor: totalPedido, descricaoVale: "" }] : [];
}

function parcelasVale(pedido = {}) {
  return formasPagamento(pedido).filter((item) => item.forma === "vale");
}

function valorEmVale(pedido = {}) {
  return parcelasVale(pedido).reduce((soma, item) => soma + numero(item.valor), 0);
}

function descricaoDoVale(pedido = {}) {
  const descricoes = [
    ...new Set(
      parcelasVale(pedido)
        .map((item) => String(item.descricaoVale || "").trim())
        .filter(Boolean)
    ),
  ];

  return descricoes.join(" | ") || String(pedido?.valeDescricao || "").trim() || "—";
}

const moeda = (valor) =>
  `R$ ${numero(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const ValesAdmin = () => {
  const [eventos, setEventos] = useState([]);
  const [eventoSelecionado, setEventoSelecionado] = useState("");
  const [vales, setVales] = useState([]);
  const [totalVales, setTotalVales] = useState(0);
  const [loading, setLoading] = useState(false);

  const [pedidoRemover, setPedidoRemover] = useState(null);
  const [novaForma, setNovaForma] = useState("pix");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);

  const [cupons, setCupons] = useState([]);
  const [loadingCupons, setLoadingCupons] = useState(false);
  const [salvandoCupom, setSalvandoCupom] = useState(false);
  const [formCupom, setFormCupom] = useState({ codigo: "", descricao: "", limiteUsos: "0", dataExpiracao: "" });

  useEffect(() => {
    const fetchEventos = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEventos(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erro ao carregar eventos:", err);
      }
    };

    fetchEventos();
  }, []);

  const carregarVales = async (eventoId) => {
    if (!eventoId) {
      setVales([]);
      setTotalVales(0);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/pedidos/todos-pedidos-admin`, {
        params: { eventoId },
        headers: { Authorization: `Bearer ${token}` },
      });

      const pedidos = Array.isArray(res.data) ? res.data : [];
      const somenteVales = pedidos
        .filter((pedido) => valorEmVale(pedido) > 0)
        .map((pedido) => ({
          ...pedido,
          valorEmVale: valorEmVale(pedido),
          descricaoValeExibicao: descricaoDoVale(pedido),
        }));

      setVales(somenteVales);
      setTotalVales(somenteVales.reduce((acc, pedido) => acc + pedido.valorEmVale, 0));
    } catch (err) {
      console.error("Erro ao carregar vales:", err);
      setVales([]);
      setTotalVales(0);
    } finally {
      setLoading(false);
    }
  };

  const carregarCupons = async (eventoId) => {
    if (!eventoId) { setCupons([]); return; }
    try {
      setLoadingCupons(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/pedidos/cupons-vale/listar`, {
        params: { eventoId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCupons(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao carregar cupons de vale:", err);
      setCupons([]);
    } finally { setLoadingCupons(false); }
  };

  const criarCupom = async (event) => {
    event.preventDefault();
    if (!eventoSelecionado) return alert("Selecione um evento.");
    if (!formCupom.codigo.trim()) return alert("Informe o código do cupom.");
    try {
      setSalvandoCupom(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/pedidos/cupons-vale`, {
        eventoId: eventoSelecionado,
        codigo: formCupom.codigo,
        descricao: formCupom.descricao,
        limiteUsos: Number(formCupom.limiteUsos || 0),
        dataExpiracao: formCupom.dataExpiracao || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (!res?.data?.cupom) throw new Error("Cupom não retornado pela API.");
      setFormCupom({ codigo: "", descricao: "", limiteUsos: "0", dataExpiracao: "" });
      await carregarCupons(eventoSelecionado);
      alert("Cupom criado com sucesso.");
    } catch (err) {
      console.error("Erro ao criar cupom:", err);
      alert(err?.response?.data?.mensagem || err?.message || "Erro ao criar cupom.");
    } finally { setSalvandoCupom(false); }
  };

  const alterarStatusCupom = async (cupom) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/pedidos/cupons-vale/${cupom._id}/status`, { ativo: !cupom.ativo }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await carregarCupons(eventoSelecionado);
    } catch (err) {
      console.error("Erro ao alterar cupom:", err);
      alert(err?.response?.data?.mensagem || "Não foi possível alterar o cupom.");
    }
  };

  const handleEventoChange = async (eventoId) => {
    setEventoSelecionado(eventoId);
    await Promise.all([carregarVales(eventoId), carregarCupons(eventoId)]);
  };

  const handleBaixarPdf = () => {
    if (!eventoSelecionado) return;
    window.open(
      `${API}/relatorios/vales-pdf?eventoId=${encodeURIComponent(eventoSelecionado)}`,
      "_blank"
    );
  };

  const abrirRemocao = (pedido) => {
    setPedidoRemover(pedido);
    setNovaForma("pix");
    setMotivo("");
  };

  const fecharRemocao = () => {
    if (salvando) return;
    setPedidoRemover(null);
    setNovaForma("pix");
    setMotivo("");
  };

  const confirmarRemocao = async () => {
    if (!pedidoRemover?._id) return;

    try {
      setSalvando(true);
      const token = localStorage.getItem("token");
      const resposta = await axios.put(
        `${API}/pedidos/remover-vale/${pedidoRemover._id}`,
        {
          novaForma,
          motivo,
          usuario: "Admin",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!resposta?.data?.pedido) {
        throw new Error("A API não retornou o pedido atualizado.");
      }

      await carregarVales(eventoSelecionado);
      fecharRemocao();
      alert("Vale removido e pagamento atualizado com sucesso.");
    } catch (err) {
      console.error("Erro ao remover vale:", err);
      alert(
        err?.response?.data?.mensagem ||
        err?.response?.data?.erro ||
        err?.message ||
        "Não foi possível remover o vale."
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="financeiro-admin-container">
      <h2>
        <FaMoneyBillWave /> Vales do Evento
      </h2>

      <div className="filtro-evento">
        <label>
          <FaFilter /> Selecione o Evento:
        </label>
        <select value={eventoSelecionado} onChange={(e) => handleEventoChange(e.target.value)}>
          <option value="">Todos</option>
          {eventos.map((ev) => (
            <option key={ev._id} value={ev._id}>
              {ev.nome}
            </option>
          ))}
        </select>

        <button
          className="btn-baixar"
          onClick={handleBaixarPdf}
          disabled={!eventoSelecionado}
          style={{ marginLeft: 12 }}
          title="Baixar PDF com todos os vales deste evento"
        >
          <FaFileDownload /> Baixar PDF de Vales
        </button>
      </div>

      <div style={{ marginTop: 22, marginBottom: 24, padding: 20, background: "linear-gradient(145deg, #2a2a2a, #333)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 12 }}>
        <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8, color: "#fdd212" }}><FaKey /> Cupons de Vale</h3>
        <p style={{ marginTop: -4, color: "#bbb", fontSize: 13 }}>O cupom aprova automaticamente o pedido como pago em Vale e só funciona no evento selecionado.</p>

        <form onSubmit={criarCupom} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, alignItems: "end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, color: "#ddd", fontSize: 12 }}>Código
            <input value={formCupom.codigo} onChange={(e) => setFormCupom((f) => ({ ...f, codigo: e.target.value.toUpperCase().replace(/\s+/g, "") }))} placeholder="JRSIQUEIRA" maxLength={40} disabled={!eventoSelecionado || salvandoCupom} style={{ padding: 10, borderRadius: 8, border: "1px solid #555", background: "#222", color: "#fff" }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, color: "#ddd", fontSize: 12 }}>Descrição (opcional)
            <input value={formCupom.descricao} onChange={(e) => setFormCupom((f) => ({ ...f, descricao: e.target.value }))} placeholder="Ex.: Cupom do dono do evento" disabled={!eventoSelecionado || salvandoCupom} style={{ padding: 10, borderRadius: 8, border: "1px solid #555", background: "#222", color: "#fff" }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, color: "#ddd", fontSize: 12 }}>Limite de usos
            <input type="number" min="0" value={formCupom.limiteUsos} onChange={(e) => setFormCupom((f) => ({ ...f, limiteUsos: e.target.value }))} disabled={!eventoSelecionado || salvandoCupom} style={{ padding: 10, borderRadius: 8, border: "1px solid #555", background: "#222", color: "#fff" }} />
            <small style={{ color: "#888" }}>0 = ilimitado</small>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, color: "#ddd", fontSize: 12 }}>Expira em (opcional)
            <input type="date" value={formCupom.dataExpiracao} onChange={(e) => setFormCupom((f) => ({ ...f, dataExpiracao: e.target.value }))} disabled={!eventoSelecionado || salvandoCupom} style={{ padding: 10, borderRadius: 8, border: "1px solid #555", background: "#222", color: "#fff" }} />
          </label>
          <button type="submit" className="btn-baixar" disabled={!eventoSelecionado || salvandoCupom} style={{ margin: 0, minHeight: 40, whiteSpace: "nowrap" }}><FaPlus /> {salvandoCupom ? "Criando..." : "Criar cupom"}</button>
        </form>

        <div style={{ marginTop: 18, overflowX: "auto" }}>
          {loadingCupons ? <p style={{ color: "#bbb" }}>Carregando cupons...</p> : cupons.length === 0 ? <p style={{ color: "#888" }}>Nenhum cupom criado para este evento.</p> : (
            <table className="extrato-tabela" style={{ minWidth: 760 }}>
              <thead><tr><th>Cupom</th><th>Descrição</th><th>Usos</th><th>Expiração</th><th>Status</th><th>Ação</th></tr></thead>
              <tbody>{cupons.map((cupom) => {
                const expirado = cupom.dataExpiracao && new Date(cupom.dataExpiracao).getTime() < Date.now();
                const esgotado = Number(cupom.limiteUsos || 0) > 0 && Number(cupom.usos || 0) >= Number(cupom.limiteUsos || 0);
                return <tr key={cupom._id}>
                  <td><strong style={{ color: "#fdd212", letterSpacing: ".04em" }}>{cupom.codigo}</strong></td>
                  <td>{cupom.descricao || "—"}</td>
                  <td>{cupom.usos || 0}{Number(cupom.limiteUsos || 0) > 0 ? ` / ${cupom.limiteUsos}` : " / ∞"}</td>
                  <td>{cupom.dataExpiracao ? new Date(cupom.dataExpiracao).toLocaleDateString("pt-BR") : "Sem expiração"}</td>
                  <td>{!cupom.ativo ? "Inativo" : expirado ? "Expirado" : esgotado ? "Esgotado" : "Ativo"}</td>
                  <td><button type="button" onClick={() => alterarStatusCupom(cupom)} className="btn-baixar" style={{ margin: 0, whiteSpace: "nowrap" }}><FaPowerOff /> {cupom.ativo ? "Desativar" : "Ativar"}</button></td>
                </tr>;
              })}</tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid-resumo-admin">
        <div className="grid-item-admin">
          <h4>Total em Vales</h4>
          <p>{moeda(totalVales)}</p>
        </div>
      </div>

      <div className="tabela-financeira">
        <h2>
          <FaTicketAlt /> Lista de Vales
        </h2>

        <div className="table-container">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Carregando vales...</p>
            </div>
          ) : vales.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum vale encontrado para esse evento.</p>
            </div>
          ) : (
            <table className="extrato-tabela">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Descrição do Vale</th>
                  <th>Nome do Vaqueiro</th>
                  <th>Senhas</th>
                  <th>Valor em Vale</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {vales.map((pedido) => (
                  <tr key={pedido._id}>
                    <td>
                      {pedido.dataCriacao
                        ? new Date(pedido.dataCriacao).toLocaleString("pt-BR")
                        : "—"}
                    </td>
                    <td>{pedido.descricaoValeExibicao}</td>
                    <td>{pedido.nomeVaqueiro || "—"}</td>
                    <td>
                      {Array.isArray(pedido.senhas) && pedido.senhas.length
                        ? pedido.senhas.map((senha) => senha.numero).join(", ")
                        : "—"}
                    </td>
                    <td>{moeda(pedido.valorEmVale)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-baixar"
                        onClick={() => abrirRemocao(pedido)}
                        title="Substituir apenas a parcela em vale por outra forma de pagamento"
                        style={{ margin: 0, whiteSpace: "nowrap" }}
                      >
                        <FaUndoAlt /> Remover do vale
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {pedidoRemover && (
        <div
          onClick={fecharRemocao}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 520,
              maxWidth: "100%",
              background: "#fff",
              color: "#222",
              borderRadius: 10,
              padding: 24,
              boxShadow: "0 20px 55px rgba(0,0,0,.35)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Remover do vale</h3>
            <p>
              <strong>Vaqueiro:</strong> {pedidoRemover.nomeVaqueiro || "—"}
              <br />
              <strong>Valor em vale:</strong> {moeda(valorEmVale(pedidoRemover))}
              <br />
              <strong>Descrição:</strong> {descricaoDoVale(pedidoRemover)}
            </p>
            <p style={{ fontSize: 13, color: "#555" }}>
              Somente a parcela em vale será substituída. O total do pedido não será alterado.
            </p>

            <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
              Nova forma de pagamento
            </label>
            <select
              value={novaForma}
              onChange={(event) => setNovaForma(event.target.value)}
              style={{ width: "100%", padding: 10, marginBottom: 16 }}
              disabled={salvando}
            >
              <option value="pix">Pix</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="cartao">Cartão</option>
              <option value="boleto">Boleto</option>
              <option value="cheque">Cheque</option>
              <option value="transferencia">Transferência</option>
              <option value="manual">Manual</option>
            </select>

            <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
              Motivo / observação (opcional)
            </label>
            <textarea
              rows={3}
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              placeholder="Ex.: cliente acertou o valor via Pix."
              style={{ width: "100%", padding: 10, resize: "vertical", marginBottom: 18 }}
              disabled={salvando}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={fecharRemocao} disabled={salvando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-baixar"
                onClick={confirmarRemocao}
                disabled={salvando}
                style={{ margin: 0 }}
              >
                {salvando ? "Salvando..." : "Confirmar remoção"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValesAdmin;
